import { memo, FunctionComponent, ReactText } from 'react';
import messages from './Messages';
import Icon from 'components/Icon';
import DistinctiveAnswers from 'components/DistinctiveAnswers';
import {
  InfoText,
  RelatedItemBox,
  ResponseInfoWrapper,
  RelatedItemInfoContainer,
  DistinctiveAnswersWrapper,
} from './styles';
import {
  IAnswersView,
  IGridAnswerView,
  IGridQuestionView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  ISubAnswer,
  ISurveyItem,
  ISurveyView,
  QuestionType,
} from 'store/survey/surveyTypes';
import { ITags } from 'store/providers/providerTypes';
import {
  QuestionBody,
  QuestionWrapper,
} from 'components/SurveyResponses/styles';
import TitleInput from 'components/SurveyResponses/TitleInput';
import AnswerView from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/AnswerView';

interface IComponentProps {
  tags: ITags['data'];
  title: string;
  answeredBy: string;
  submitDate: ReactText;
  answersList: Array<IAnswersView>;
  effectiveDate: ReactText | boolean;
  selectedTags: number[];
  selectedChoices: Array<string | number>;
  singlePagePrint?: boolean;
  distinctiveQuestions: IMultipleChoiceQuestionView[];
  relatedQuestionsList: IRelatedQuestion[];
  survey: ISurveyView | undefined;
  surveyItem?: ISurveyItem;
  providerId?: number | undefined;
}

type IRelatedQuestion = IMultipleChoiceQuestionView | IGridQuestionView;

const areChoiceValuesEqual = (
  selectedValue: string | number,
  answerValue: string | number | undefined
) => {
  if (answerValue === undefined || answerValue === null) return false;
  return String(selectedValue) === String(answerValue);
};

const RelatedResponseItem: FunctionComponent<IComponentProps> = ({
  tags,
  title,
  answeredBy,
  submitDate,
  answersList,
  effectiveDate,
  selectedTags,
  selectedChoices,
  singlePagePrint,
  relatedQuestionsList,
  distinctiveQuestions,
  survey,
  surveyItem,
  providerId,
}) => {
  const hasSelectedTags = selectedTags.length > 0;
  const hasSelectedChoices = selectedChoices.length > 0;

  // filter questions by tags
  const filteredRelatedQuestionsList = hasSelectedTags
    ? relatedQuestionsList.filter((question) => {
        return (
          question.tags &&
          selectedTags.every((selectedTagId) =>
            question.tags.some(
              (questionTag) => selectedTagId === questionTag.id
            )
          )
        );
      })
    : relatedQuestionsList;

  // filter answers by choices
  const filteredAnswersList: any = (
    hasSelectedChoices
      ? Object.values(answersList).filter((answer) => {
          if (answer.type === QuestionType.MultipleChoice) {
            const selectedList = Array.isArray(
              (answer as IMultipleChoiceAnswerView).selected
            )
              ? (answer as IMultipleChoiceAnswerView).selected
              : [];

            if (typeof answer.value === 'string' && answer.value !== '') {
              return selectedChoices.some((choice) =>
                areChoiceValuesEqual(choice, answer.value)
              );
            } else if (
              (answer as IMultipleChoiceAnswerView).values &&
              (answer as IMultipleChoiceAnswerView).values.length > 0
            ) {
              return (answer as IMultipleChoiceAnswerView).values.find(
                (choiceValue: string) =>
                  selectedChoices.some((choice) =>
                    areChoiceValuesEqual(choice, choiceValue)
                  )
              );
            } else {
              return selectedList.find((tagId: number) =>
                selectedChoices.some((choice) =>
                  areChoiceValuesEqual(choice, tagId)
                )
              );
            }
          } else {
            return (answer as IGridAnswerView).subAnswers.find(
              (subAnswer: ISubAnswer) =>
                selectedChoices.some((choice) =>
                  areChoiceValuesEqual(choice, subAnswer.value)
                )
            );
          }
        })
      : answersList
  ).filter((ansewr) => {
    return (
      !!ansewr &&
      !!filteredRelatedQuestionsList.find(
        (question: IRelatedQuestion) => question.id === ansewr.questionId
      )
    );
  });

  if (filteredRelatedQuestionsList?.length === 0 || !filteredAnswersList[0]) {
    return <div />;
  }

  return (
    <RelatedItemBox singlePagePrint={!!singlePagePrint}>
      <RelatedItemInfoContainer>
        <ResponseInfoWrapper>
          <span>
            {title && title.length !== 0 && (
              <InfoText align="flex-start">
                <Icon type="align-justify" />
                <span>{messages.responseTitle}</span>
                <span>{title}</span>
              </InfoText>
            )}
            <InfoText>
              <Icon type="user" />
              <span>{messages.answeredBy}</span>
              <span>{answeredBy}</span>
            </InfoText>
          </span>
        </ResponseInfoWrapper>
        {distinctiveQuestions && (
          <DistinctiveAnswersWrapper>
            <DistinctiveAnswers
              tags={tags}
              answers={answersList}
              questions={distinctiveQuestions}
            />
          </DistinctiveAnswersWrapper>
        )}
        <ResponseInfoWrapper className="align-center">
          <span>
            <InfoText>
              <Icon type="calendar" />
              <span>{messages.submitDate}</span>
              <span>{submitDate}</span>
            </InfoText>
            <InfoText>
              <Icon type="clock" />
              <span>{messages.effectiveDate}</span>
              <span>{effectiveDate}</span>
            </InfoText>
          </span>
        </ResponseInfoWrapper>
      </RelatedItemInfoContainer>
      {/*<div> {filteredAnswersList.size}</div>*/}
      {/*<div> {JSON.stringify(filteredAnswersList)}</div>*/}
      {/*<div> ---------------------------------- </div>*/}
      {/*<div> {JSON.stringify(filteredRelatedQuestionsList)}</div>*/}
      {filteredAnswersList
        .sort((a: any, b: any) => {
          const aQuestion = filteredRelatedQuestionsList.find(
            (q) => q.id === a.questionId
          );
          const bQuestion = filteredRelatedQuestionsList.find(
            (q) => q.id === b.questionId
          );
          if (aQuestion && bQuestion) {
            return aQuestion.order > bQuestion.order ? 1 : -1;
          }
          return 0;
        })
        .map(
          (
            answer: IGridAnswerView | IMultipleChoiceAnswerView,
            idx: number
          ) => {
            const relatedQuestion = filteredRelatedQuestionsList.find(
              (question: IRelatedQuestion) => question.id === answer.questionId
            );
            if (relatedQuestion) {
              return (
                <QuestionWrapper key={relatedQuestion.id} disabled={false}>
                  <TitleInput
                    fields={relatedQuestion.fields || []}
                    hint={relatedQuestion.hint}
                    required={relatedQuestion.answerRequired}
                    title={relatedQuestion.title}
                    tags={relatedQuestion.tags}
                    allTags={tags}
                    badge={undefined}
                    indexNumber={relatedQuestion.indexOrder ?? idx + 1}
                    survey={survey}
                  />
                  <QuestionBody>
                    <AnswerView
                      survey={survey}
                      surveyItem={{ data: surveyItem }}
                      answer={answer}
                      question={relatedQuestion}
                      isExam={false}
                      showCorrectAnswers={false}
                      providerId={providerId || surveyItem?.providerId}
                    />
                  </QuestionBody>
                </QuestionWrapper>
              );
            } else {
              if (selectedTags.length > 0) {
                return <div key={idx} />;
              } else {
                return <p key={idx}>{messages.noQuestionFind}</p>;
              }
            }
          }
        )}
    </RelatedItemBox>
  );
};

export default memo(RelatedResponseItem);
